import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { readNotification, readAllNotifications, removeNotification } from '../redux/actions/notificationActions';
import notificationService from '../services/notificationService';
import { useLocation } from 'react-router-dom';

const LIMIT = 20;

const getNotifIcon = (type) => {
  switch (type) {
    case 'submission_status':  return 'description';
    case 'order_status':       return 'shopping_cart';
    case 'new_submission':     return 'upload_file';
    case 'new_order':          return 'add_shopping_cart';
    default:                   return 'notifications';
  }
};

const getNotifIconClass = (type) => {
  switch (type) {
    case 'submission_status':  return 'notif-icon--submission';
    case 'order_status':       return 'notif-icon--order';
    case 'new_submission':     return 'notif-icon--new-submission';
    case 'new_order':          return 'notif-icon--new-order';
    default:                   return 'notif-icon--default';
  }
};

const getNotifTypeLabel = (type) => {
  switch (type) {
    case 'submission_status':  return 'Soumission';
    case 'order_status':       return 'Commande';
    case 'new_submission':     return 'Nouvelle soumission';
    case 'new_order':          return 'Nouvelle commande';
    default:                   return 'Système';
  }
};

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return '—';
  }
};

const Notifications = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [processingIds, setProcessingIds] = useState([]);

  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const fetchPage = useCallback(async (pageNum, onlyUnread, replace) => {
    if (replace) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    try {
      const response = await notificationService.getNotifications(onlyUnread, LIMIT, pageNum);
      const { notifications: items, hasMore: more, total: count } = response.data;
      setNotifications(prev => replace ? items : [...prev, ...items]);
      setHasMore(more);
      setTotal(count);
      setPage(pageNum);
    } catch {
      setError('Impossible de charger les notifications.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1, filter === 'unread', true);
  }, [filter, fetchPage]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchPage(page + 1, filter === 'unread', false);
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 }
    );

    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, page, filter, fetchPage]);

  const withProcessing = (id, fn) => async () => {
    if (processingIds.includes(id)) return;
    setProcessingIds(prev => [...prev, id]);
    try { await fn(); } finally {
      setProcessingIds(prev => prev.filter(x => x !== id));
    }
  };

  const handleMarkAsRead = (id) => withProcessing(id, async () => {
    await dispatch(readNotification(id));
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
  })();

  const handleDelete = (id) => withProcessing(id, async () => {
    await dispatch(removeNotification(id));
    setNotifications(prev => prev.filter(n => n.id !== id));
    setTotal(prev => prev - 1);
  })();

  const handleMarkAllAsRead = async () => {
    await dispatch(readAllNotifications());
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="notif-page">
      <div className="page-header">
        <h1>Notifications</h1>
        {unreadCount > 0 && (
          <button className="btn btn-outline-secondary" onClick={handleMarkAllAsRead}>
            <i className="material-icons">done_all</i>
            Tout marquer comme lu
          </button>
        )}
      </div>

      {/* Filtres */}
      <div className="filters-card">
        <div className="notif-filter-tabs">
          <button
            className={`notif-filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Toutes
            {total > 0 && <span className="notif-count-badge">{total}</span>}
          </button>
          <button
            className={`notif-filter-btn ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => setFilter('unread')}
          >
            Non lues
            {filter === 'unread' && total > 0 && <span className="notif-count-badge notif-count-unread">{total}</span>}
            {filter !== 'unread' && unreadCount > 0 && <span className="notif-count-badge notif-count-unread">{unreadCount}</span>}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement des notifications...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="empty-state">
          <i className="material-icons">notifications_off</i>
          <h2>Aucune notification</h2>
          <p>{filter === 'unread' ? 'Vous n\'avez aucune notification non lue.' : 'Vous n\'avez pas encore reçu de notifications.'}</p>
        </div>
      ) : (
        <div className="dashboard-card">
          <div className="table-responsive" ref={scrollContainerRef}>
            <table className="table notif-table">
              <thead>
                <tr>
                  <th className="notif-col-type">Type</th>
                  <th className="notif-col-message">Message</th>
                  <th className="notif-col-date">Date</th>
                  <th className="notif-col-status">Statut</th>
                  <th className="notif-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map(n => (
                  <tr key={n.id} className={!n.is_read ? 'notif-row-unread' : ''}>
                    <td>
                      <div className="notif-type-cell">
                        <i className={`material-icons notification-icon ${getNotifIconClass(n.type)}`}>
                          {getNotifIcon(n.type)}
                        </i>
                        <span className="notif-type-label">{getNotifTypeLabel(n.type)}</span>
                      </div>
                    </td>
                    <td className="notif-col-message">
                      <span className="notif-message-text">{n.message}</span>
                      {n.action_link && (
                        <a
                          href={isAdminRoute && n.action_link.startsWith('/') ? `/admin${n.action_link}` : n.action_link}
                          className="notif-action-link"
                        >
                          {n.action_text || 'Voir'}
                        </a>
                      )}
                    </td>
                    <td className="notif-col-date">
                      <span className="notif-date">{formatDate(n.created_at)}</span>
                    </td>
                    <td>
                      {n.is_read
                        ? <span className="status-badge notif-badge-read">Lu</span>
                        : <span className="status-badge notif-badge-unread">Non lu</span>
                      }
                    </td>
                    <td>
                      <div className="table-actions">
                        {!n.is_read && (
                          <button
                            className="btn-icon"
                            title="Marquer comme lu"
                            onClick={() => handleMarkAsRead(n.id)}
                            disabled={processingIds.includes(n.id)}
                          >
                            <i className="material-icons">check</i>
                          </button>
                        )}
                        <button
                          className="btn-icon danger"
                          title="Supprimer"
                          onClick={() => handleDelete(n.id)}
                          disabled={processingIds.includes(n.id)}
                        >
                          <i className="material-icons">delete_outline</i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div ref={sentinelRef} style={{ height: 1 }} />

            {loadingMore && (
              <div className="loading-more">
                <div className="spinner spinner-sm"></div>
                <span>Chargement...</span>
              </div>
            )}
            {!hasMore && notifications.length > 0 && (
              <p className="list-end-message">Toutes les notifications sont affichées</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
